import { diffLines } from "diff";

export type DiffSide = "LEFT" | "RIGHT";
export type DiffRowKind = "context" | "add" | "delete";

export interface UnifiedDiffRow {
  id: string;
  kind: DiffRowKind;
  oldLine?: number;
  newLine?: number;
  text: string;
}

export interface CollapsedDiffRows {
  id: string;
  kind: "collapsed";
  hiddenLines: number;
  oldStart?: number;
  oldEnd?: number;
  newStart?: number;
  newEnd?: number;
  rows: UnifiedDiffRow[];
}

export type DisplayDiffRow = UnifiedDiffRow | CollapsedDiffRows;

export interface SplitDiffRow {
  id: string;
  kind: "split";
  /** Old-file side: a deletion or the shared context row. */
  left?: UnifiedDiffRow;
  /** New-file side: an addition or the shared context row. */
  right?: UnifiedDiffRow;
}

export type SplitDisplayRow = SplitDiffRow | CollapsedDiffRows;

export interface DiffFocusRange {
  side: DiffSide;
  startLine: number;
  endLine: number;
}

function linesInChange(value: string) {
  if (!value) return [];
  const normalized = value.replaceAll("\r\n", "\n");
  const lines = normalized.split("\n");
  if (normalized.endsWith("\n")) lines.pop();
  return lines;
}

export function buildUnifiedDiff(base: string, head: string): UnifiedDiffRow[] {
  let oldLine = 1;
  let newLine = 1;
  let ordinal = 0;
  const rows: UnifiedDiffRow[] = [];

  for (const change of diffLines(base, head, { oneChangePerToken: true })) {
    const kind: DiffRowKind = change.added ? "add" : change.removed ? "delete" : "context";
    for (const text of linesInChange(change.value)) {
      const row: UnifiedDiffRow = {
        id: `${kind}:${oldLine}:${newLine}:${ordinal}`,
        kind,
        oldLine: kind === "add" ? undefined : oldLine,
        newLine: kind === "delete" ? undefined : newLine,
        text,
      };
      rows.push(row);
      if (kind !== "add") oldLine += 1;
      if (kind !== "delete") newLine += 1;
      ordinal += 1;
    }
  }

  return rows;
}

/**
 * Pair display rows into side-by-side columns: context rows span both sides,
 * and each run of deletions is zipped index-wise against the additions that
 * replace it, padding the shorter side with an empty cell. Collapsed spans
 * pass through untouched so folding behaves identically in both layouts.
 */
export function buildSplitRows(rows: DisplayDiffRow[]): SplitDisplayRow[] {
  const display: SplitDisplayRow[] = [];
  const deletes: UnifiedDiffRow[] = [];
  const adds: UnifiedDiffRow[] = [];

  const flushChanged = () => {
    for (let index = 0; index < Math.max(deletes.length, adds.length); index += 1) {
      const left = deletes[index];
      const right = adds[index];
      display.push({ id: `split:${left?.id ?? "-"}:${right?.id ?? "-"}`, kind: "split", left, right });
    }
    deletes.length = 0;
    adds.length = 0;
  };

  for (const row of rows) {
    if (row.kind === "delete") {
      // A new deletion run after additions means a separate change block.
      if (adds.length > 0) flushChanged();
      deletes.push(row);
      continue;
    }
    if (row.kind === "add") {
      adds.push(row);
      continue;
    }
    flushChanged();
    if (row.kind === "collapsed") display.push(row);
    else display.push({ id: `split:${row.id}`, kind: "split", left: row, right: row });
  }
  flushChanged();

  return display;
}

export function diffStats(rows: UnifiedDiffRow[]) {
  return rows.reduce(
    (total, row) => ({
      additions: total.additions + Number(row.kind === "add"),
      deletions: total.deletions + Number(row.kind === "delete"),
    }),
    { additions: 0, deletions: 0 },
  );
}

export function rowMatchesFocus(row: UnifiedDiffRow, focus?: DiffFocusRange) {
  if (!focus) return false;
  const line = focus.side === "LEFT" ? row.oldLine : row.newLine;
  return line !== undefined && line >= focus.startLine && line <= focus.endLine;
}

function rangeBounds(rows: UnifiedDiffRow[], side: DiffSide) {
  const values = rows.flatMap((row) => {
    const line = side === "LEFT" ? row.oldLine : row.newLine;
    return line === undefined ? [] : [line];
  });
  return values.length === 0 ? {} : { start: values[0], end: values.at(-1) };
}

export function collapseUnchangedRows(
  rows: UnifiedDiffRow[],
  {
    contextLines = 3,
    expanded = new Set<string>(),
    focus,
    minimumHiddenLines = 6,
    visibleRanges = [],
  }: {
    contextLines?: number;
    expanded?: ReadonlySet<string>;
    focus?: DiffFocusRange;
    minimumHiddenLines?: number;
    visibleRanges?: readonly DiffFocusRange[];
  } = {},
): DisplayDiffRow[] {
  const visible = rows.map((row) => row.kind !== "context" || rowMatchesFocus(row, focus) || visibleRanges.some((range) => rowMatchesFocus(row, range)));

  for (const [index, shown] of [...visible.entries()]) {
    if (!shown) continue;
    const start = Math.max(0, index - contextLines);
    const end = Math.min(rows.length - 1, index + contextLines);
    for (let nearby = start; nearby <= end; nearby += 1) visible[nearby] = true;
  }

  const display: DisplayDiffRow[] = [];
  let index = 0;
  while (index < rows.length) {
    if (visible[index]) {
      display.push(rows[index]!);
      index += 1;
      continue;
    }

    const start = index;
    while (index < rows.length && !visible[index]) index += 1;
    const hidden = rows.slice(start, index);
    if (hidden.length < minimumHiddenLines) {
      display.push(...hidden);
      continue;
    }

    const left = rangeBounds(hidden, "LEFT");
    const right = rangeBounds(hidden, "RIGHT");
    const id = `collapsed:${left.start ?? 0}-${left.end ?? 0}:${right.start ?? 0}-${right.end ?? 0}`;
    if (expanded.has(id)) {
      display.push(...hidden);
      continue;
    }
    display.push({
      id,
      kind: "collapsed",
      hiddenLines: hidden.length,
      oldStart: left.start,
      oldEnd: left.end,
      newStart: right.start,
      newEnd: right.end,
      rows: hidden,
    });
  }

  return display;
}
